<template>
  <div class="pa-6">
    <Breadcrumbs
      glCodeText="Data Processing"
      :glCodeInfo="detailsTitle"
      :activeCommentary="{}"
      :customStatus="detailsStatus"
      :getStatusLabel="() => ''"
      data-testid="data-processing-details-breadcrumbs"
      @back="goBack"
    />
    
    <v-card v-if="selectedItem" class="mt-4 pa-3" outlined>
        <v-tabs v-model="activeTab" class="data-processing-details__tabs" background-color="transparent">
            <v-tab>Details</v-tab>
            <v-tab>Errors</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="pt-4">
            <v-tab-item>
                <div class="d-flex flex-column gap-4">

                    <!-- Count summary cards -->
                    <div v-if="countSections.length > 0" class="d-flex align-center gap-4">
                         <CountSection 
                            v-for="(section, idx) in countSections"
                            :key="idx"
                            :count="section.count"
                            :networkLabel="section.label"
                            countLabel=""
                            class="gap-10"
                         />
                    </div>

                    <!-- Period List Section (gldata & drilldown) -->
                    <v-card v-if="showPeriodSection" class="pa-3 d-flex flex-column gap-4" outlined flat>
                        <div class="d-flex align-center justify-space-between">
                            <h4>Period List</h4>
                            <CountSection 
                                v-if="periodList.length > 0"
                                :count="periodList.length"
                                networkLabel="Total Periods"
                                countLabel=""
                                class="gap-2 flex-row-reverse"
                                />
                        </div>
                        <template v-if="periodList.length > 0">
                            <div class="d-flex flex-wrap gap-2">
                                <v-chip
                                    v-for="(period, index) in visiblePeriodList"
                                    :key="'p-' + index"
                                    class="chip-choose"
                                >
                                    <span class="categorystype">{{ period }}</span>
                                </v-chip>
                            </div>
                            <div v-if="periodList.length > 20" class="mt-2">
                                <base-button
                                    primary 
                                    @click="showAllPeriods = !showAllPeriods"
                                >
                                    {{ showAllPeriods ? 'View Less' : `View More (+${periodList.length - 20})` }}
                                </base-button>
                            </div>
                        </template>
                        <v-alert v-else type="info" text class="mb-0">
                            No periods available
                        </v-alert>
                    </v-card>

                    <!-- RU List Section (gldata & drilldown) -->
                    <v-card v-if="showRuSection" class="pa-3 d-flex flex-column gap-4" outlined flat>
                        <div class="d-flex align-center justify-space-between">
                            <h4>Reporting Units (RU) List</h4>
                            <CountSection 
                                v-if="ruList.length > 0"
                                :count="ruList.length"
                                networkLabel="Total RUs"
                                countLabel=""
                                class="gap-2 flex-row-reverse"
                                />
                        </div>
                        <template v-if="ruList.length > 0">
                            <div class="d-flex flex-wrap gap-2">
                                <v-chip
                                    v-for="(ru, index) in visibleRuList"
                                    :key="index"
                                    class="chip-choose"
                                >
                                    <span class="categorystype">{{ ru }}</span>
                                </v-chip>
                            </div>
                            <div v-if="ruList.length > 20" class="mt-2">
                                <base-button
                                    primary 
                                    @click="showAllRus = !showAllRus"
                                >
                                    {{ showAllRus ? 'View Less' : `View More (+${ruList.length - 20})` }}
                                </base-button>
                            </div>
                        </template>
                        <v-alert v-else type="info" text class="mb-0">
                            No RUs available
                        </v-alert>
                    </v-card>

                    <!-- Discrepancy Cycles Section (drilldown only) -->
                    <v-card v-if="showDiscrepancySection" class="pa-3 d-flex flex-column gap-4" outlined flat>
                        <div class="d-flex align-center justify-space-between">
                            <h4>RUs With Mismatches</h4>
                            <CountSection 
                                v-if="discrepancyCycles.length > 0"
                                :count="discrepancyCycles.length"
                                networkLabel="Total Mismatches"
                                countLabel=""
                                class="gap-2 flex-row-reverse"
                                />
                        </div>
                        <template v-if="discrepancyCycles.length > 0">
                            <div class="d-flex flex-wrap gap-2">
                                <v-chip
                                    v-for="(cycle, index) in discrepancyCycles"
                                    :key="'dc-' + index"
                                    class="chip-choose"
                                    color="error"
                                    text-color="white"
                                >
                                    <span class="categorystype">{{ cycle }}</span>
                                </v-chip>
                            </div>
                        </template>
                        <v-alert v-else type="success" text class="mb-0">
                            No mismatches found
                        </v-alert>
                    </v-card>

                    <!-- No data at all (only for items with no category match) -->
                    <v-alert v-if="countSections.length === 0 && !showPeriodSection && !showRuSection" type="info" text class="mt-2">
                        No data found
                    </v-alert>
                </div>
            </v-tab-item>

            <v-tab-item>
                <div class="">
                    <div v-if="errorItems.length > 0">
                        <lb-datatablenew
                            :headers="errorHeaders"
                            :items="errorItems"
                            :enableslot="['affected_records']"
                            :hide-default-footer="true"
                            class="elevation-0 border"
                        >
                            <template v-slot:affected_records="{ item }">
                                <div class="d-flex flex-wrap gap-2">
                                    <v-chip
                                        v-for="(record, index) in (item.affected_records || [])"
                                        :key="index"
                                        class="email-chip"
                                        outlined
                                    >
                                        <span>{{ record }}</span>
                                    </v-chip>
                                </div>
                            </template>
                        </lb-datatablenew>
                    </div>
                     <v-alert v-else type="success" text>
                        No error conditions detected
                    </v-alert>
                </div>
            </v-tab-item>
        </v-tabs-items>
    </v-card>
    <v-alert v-else type="info" class="mt-4">
        No details available. Please go back and select a record again.
    </v-alert>
  </div>
</template>

<script>
import { DATA_PROCESSING } from '../../../utils/constants';
import Breadcrumbs from '../../../components/Breadcrumbs.vue';
import CountSection from '../../GlSelection/CountSection.vue';

export default {
    name: 'DataProcessingDetails',
    components: {
        Breadcrumbs,
        CountSection
    },
    data() {
        return {
            selectedItem: null,
            activeTab: 0,
            showAllRus: false,
            showAllPeriods: false
        }
    },
    created() {
        if (this.$route.params.item) {
            this.selectedItem = this.$route.params.item;
        } else {
             const storedItem = sessionStorage.getItem('selectedDataProcessingItem');
             if (storedItem) {
                 try {
                     this.selectedItem = JSON.parse(storedItem);
                 } catch (e) {
                     console.error('[DataProcessingDetails]: Failed to parse stored item', e);
                     this.$store.commit('sbError', 'Failed to load record details. Please go back and try again.');
                 }
             }
        }
    },
    computed: {
        rawItem() {
            return this.selectedItem?.raw || {};
        },
        itemCategory() {
            if (!this.selectedItem) return '';
            return DATA_PROCESSING.PROCESS_CONFIG[this.selectedItem.code]?.category || '';
        },
        detailsTitle() {
            if (!this.selectedItem) return '';
            return this.selectedItem.__codename || this.selectedItem.code;
        },
        detailsStatus() {
            if (!this.selectedItem) return null;
            
            if (this.selectedItem.error_flag) {
                return { variant: 'rejected', label: 'Error' };
            }
            if (this.selectedItem.status === 'Completed' || this.selectedItem.status === 'done') {
                return { variant: 'approved', label: 'Completed' };
            }
            return { variant: 'pending-approval', label: this.selectedItem.status || 'Pending' };
        },
        countSections() {
            if (!this.selectedItem) return [];
            const raw = this.selectedItem.raw || {};
            if (this.itemCategory === 'gldashboard') {
                return [
                    { count: this.roundValue(raw.delta_row_count), label: 'Records As Per ETL' },
                    { count: this.roundValue(raw.processed_line_count), label: 'Records Processed' }
                ];
            }
            if (this.itemCategory === 'drilldown') {
                return [
                    { count: this.roundValue(raw.total_amount1_parent), label: 'As Per Parent GLs' },
                    { count: this.roundValue(raw.total_amount1_sl), label: 'As Per Drill-Down' }
                ];
            }
            if (this.itemCategory === 'master') {
                return [
                    { count: Number(raw.source_master_count) || 0, label: 'As Per Source ERP' },
                    { count: Number(raw.processed_master_count) || 0, label: 'As Per Flux App' }
                ];
            }
            return [];
        },
        /** Show period & RU sections for gldata and drilldown (always shown, with empty state) */
        showPeriodSection() {
            return this.itemCategory === 'gldashboard' || this.itemCategory === 'drilldown';
        },
        showRuSection() {
            return this.itemCategory === 'gldashboard' || this.itemCategory === 'drilldown';
        },
        /** Discrepancy cycles section only for drilldown */
        showDiscrepancySection() {
            return this.itemCategory === 'drilldown';
        },
        periodList() {
            if (!this.selectedItem) return [];
            // Formatted item normalizes to period_list; raw API may use 'periods'
            const list = this.selectedItem.period_list || this.rawItem.periods || this.rawItem.period_list || [];
            return Array.isArray(list) ? list : [];
        },
        ruList() {
            if (!this.selectedItem) return [];
            // Formatted item normalizes to ru_list; raw API may use 'ru_codes'
            const list = this.selectedItem.ru_list || this.rawItem.ru_codes || this.rawItem.ru_list || [];
            return Array.isArray(list) ? list : [];
        },
        discrepancyCycles() {
            if (!this.selectedItem) return [];
            const totalDiscrepancies = this.selectedItem?.total_discrepancies || this.rawItem?.total_discrepancies || {};
            // Return RU codes (keys) from total_discrepancies object
            return Object.keys(totalDiscrepancies);
        },
        visibleRuList() {
            if (this.showAllRus) return this.ruList;
            return this.ruList.slice(0, 20);
        },
        visiblePeriodList() {
            if (this.showAllPeriods) return this.periodList;
            return this.periodList.slice(0, 20);
        },
        errorHeaders() {
            return [
                { text: 'S.No', value: 'sno', sortable: false, width: '80px' },
                { text: 'Error Code', value: 'error_code', sortable: false },
                { text: 'Description', value: 'description', sortable: false, width: '40%' },
                { text: 'Affected Records', value: 'affected_records', sortable: false, width: '40%' }
            ];
        },
        errorItems() {
            if (!this.rawItem || !Array.isArray(this.rawItem.error)) return [];
            return this.rawItem.error.map((err, index) => ({
                ...err,
                sno: index + 1
            }));
        }
    },
    methods: {
        goBack() {
             this.$router.go(-1);
        },
        roundValue(val) {
            if (val == null) return 0;
            const num = parseFloat(val);
            if (isNaN(num)) return 0;
            const str = num.toString();
            const dot = str.indexOf('.');
            if (dot === -1) return Number(num.toFixed(3));
            const limited = str.substring(0, dot) + '.' + str.substring(dot + 1, dot + 4);
            return Number(parseFloat(limited).toFixed(3));
        }
    }
}
</script>

<style scoped>
.data-processing-details__tabs .v-tab {
    text-transform: none;
    font-weight: 400;
    font-size: 16px;
    padding: var(--space-2xs) var(--space-md);
}

.data-processing-details__tabs .v-tab--active {
    color: var(--color-primary-base) !important;
    font-weight: 600;
}

.data-processing-details__tabs .v-tab:not(.v-tab--active) {
    color: var(--color-tabs-header) !important;
    opacity: 1;
}

::v-deep .data-processing-details__tabs .v-tabs-bar {
    height: 34px !important;
}
</style>
